import { type ComponentProps, type JSX, lazy, Suspense } from 'react'
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary'
import { ErrorFallback } from '#src/components/app/ErrorFallback.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
// Type-only: a value import here would pull the impl back into the initial
// bundle and undo the split. `typeof` on a type-only binding is a type query,
// which TypeScript permits and which emits no runtime import.
import type { OrderForm as OrderFormImpl } from '#src/components/order/order-form/OrderFormImpl.tsx'

// The order forms (lab/imaging/specialist) and their @medplum/health-gorilla
// deps are only reachable once a user opens an order composer — every call site
// renders this inside a Sheet or behind an edit toggle — so they load on demand
// rather than shipping in the portal's initial bundle.
const OrderFormLazy = lazy(() =>
  import('#src/components/order/order-form/OrderFormImpl.tsx').then((m) => ({
    default: m.OrderForm,
  }))
)

export function OrderForm(
  props: ComponentProps<typeof OrderFormImpl>
): JSX.Element {
  // The header's composer renders outside App's routed error boundary, so a
  // failed chunk fetch (a deploy rotates the hash under an open tab) would
  // take down the whole app rather than just the form. Own the boundary here,
  // like App does for the assistant panel.
  return (
    <ErrorBoundary
      fallbackRender={({ error, resetErrorBoundary }: FallbackProps) => (
        <ErrorFallback error={error} resetError={resetErrorBoundary} />
      )}
    >
      <Suspense fallback={<Loader className="mx-auto mt-8" />}>
        <OrderFormLazy {...props} />
      </Suspense>
    </ErrorBoundary>
  )
}
